import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ProfileStyle from "../../../assets/stylesheets/dashboard/profile.module.css";
import DashboardLayout from "../DashboardLayout";
import Logger from "../../../utils/logger";
import { AUTH0_PATHS, SECURITY_LINKS } from "../../../config/securityLinks";

const log = Logger.create("PasswordSecurityPage");

function getAuthProvider(user) {
  const sub = user?.sub ? String(user.sub) : "";
  if (sub.includes("|")) return sub.split("|")[0];

  const provider = user?.identities?.[0]?.provider;
  if (provider) return String(provider);

  return "unknown";
}

async function sendAuth0ChangePasswordEmail({ domain, clientId, email, connection }) {
  // Auth0 Change Password endpoint (public) - sends reset link email for DB users.
  const url = `https://${domain}${AUTH0_PATHS.CHANGE_PASSWORD}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      client_id: clientId,
      email,
      connection,
    }),
  });

  const text = await res.text();
  if (!res.ok) {
    const err = new Error(text || "Failed to send password reset email");
    err.status = res.status;
    throw err;
  }

  // Auth0 returns a plain text message like "We've just sent you an email ..."
  return text;
}

export default function PasswordSecurityPage() {
  const { user, logout } = useAuth0();

  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [success, setSuccess] = React.useState(null);
  const [showReloginPrompt, setShowReloginPrompt] = React.useState(false);

  const reloginTimerRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (reloginTimerRef.current) {
        clearTimeout(reloginTimerRef.current);
        reloginTimerRef.current = null;
      }
    };
  }, []);

  const provider = getAuthProvider(user);
  const isDbUser = provider === "auth0"; // Auth0 Database Connection users

  // These must be set in .env (safe for SPA; still ok because endpoint is public)
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

  // IMPORTANT: this is the Auth0 *Database Connection name* (Auth0 Dashboard > Authentication > Database)
  // Common default: "Username-Password-Authentication"
  const dbConnection =
    process.env.REACT_APP_AUTH0_DB_CONNECTION || "Username-Password-Authentication";

  const canSendReset = Boolean(domain && clientId && user?.email && isDbUser);

  const handleSendReset = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const msg = await sendAuth0ChangePasswordEmail({
        domain,
        clientId,
        email: user.email,
        connection: dbConnection,
      });

      setSuccess(msg || "Password reset email sent.");

      // UX: wait ~30 seconds, then ask user to re-login. Only logout after confirmation.
      if (reloginTimerRef.current) {
        clearTimeout(reloginTimerRef.current);
      }
      reloginTimerRef.current = setTimeout(() => {
        setShowReloginPrompt(true);
      }, 30 * 1000);
    } catch (e) {
      log.error("Failed to send password reset email", e);
      setError(
        e?.message ||
          "Failed to send password reset email. Please try again later."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmLogout = () => {
    // Clear app token first, then logout from Auth0.
    try {
      localStorage.removeItem("ACCESS_TOKEN");
    } catch {
      // ignore
    }

    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  return (
    <DashboardLayout>
      <div className={ProfileStyle["section"]}>
        <h2 className={ProfileStyle["section-title"]}>Password &amp; Security</h2>

        {/* Re-login prompt (30 seconds after sending reset email) */}
        {showReloginPrompt ? (
          <div
            role="dialog"
            aria-modal="true"
            className={ProfileStyle["password-relogin-overlay"]}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <div
              className={ProfileStyle["password-relogin-card"]}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={ProfileStyle["password-relogin-title"]}>
                Please sign in again
              </div>
              <div className={ProfileStyle["password-relogin-desc"]}>
                For your security, please sign in again after updating your password.
              </div>

              <div className={ProfileStyle["password-relogin-actions"]}>
                <button
                  type="button"
                  onClick={handleConfirmLogout}
                  className={ProfileStyle["save-button"]}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className={ProfileStyle["password-grid"]}>
          {/* Password / security actions */}
          <div className={ProfileStyle["password-card"]}>
            <div className={ProfileStyle["password-title"]}>Change password</div>

            {isDbUser ? (
              <>
                <div className={ProfileStyle["password-subtle-text"]}>
                  We'll email a password reset link to <b>{user?.email || "your email"}</b>. 
                </div>

                <ul className={ProfileStyle["password-instructions"]}>
                  <li>If you don't see it, check your spam/junk folder.</li>
                  <li>For security, you may be asked to sign in again.</li>
                </ul>

                {success ? (
                  <div className={ProfileStyle["password-success-text"]}>{success}</div>
                ) : null}

                {error ? (
                  <div className={ProfileStyle["password-error-text"]}>{error}</div>
                ) : null}

                <div className={ProfileStyle["form-footer"]}>
                  <button
                    type="button"
                    className={ProfileStyle["save-button"]}
                    onClick={handleSendReset}
                    disabled={isSaving || !canSendReset}
                    title={!canSendReset ? "Missing Auth0 config or email" : undefined}
                  >
                    {isSaving ? "Sending..." : "Send reset link"}
                  </button>
                </div>

                {!domain || !clientId ? (
                  <div className={ProfileStyle["password-config-hint"]}>
                    Missing Auth0 config: set <code>REACT_APP_AUTH0_DOMAIN</code> and
                    <code> REACT_APP_AUTH0_CLIENT_ID</code>.
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className={ProfileStyle["password-subtle-text"]}>
                  You're signed in with <b>{provider === "google-oauth2" ? "Google" : provider}</b>.
                  Password changes are managed by your sign-in provider.
                </div>
                {provider === "google-oauth2" ? (
                  <div className={ProfileStyle["password-provider-link-wrap"]}>
                    <a
                      href={SECURITY_LINKS.GOOGLE_ACCOUNT_SECURITY}
                      target="_blank"
                      rel="noreferrer"
                      className={ProfileStyle["google-security-link"]}
                    >
                      Open Google Account Security
                      <span aria-hidden="true">↗</span>
                    </a>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
